import { EnhancedBaseService } from '../base/EnhancedBaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

export type ArchiveScope = 'all' | 'projects' | 'conversations';
export type ArchiveItemKind = 'project' | 'conversation';

export interface ArchiveItem {
  id: string;
  kind: ArchiveItemKind;
  title: string;
  preview: string | null;
  parentTitle: string | null;
  archivedAt: string;
  updatedAt: string | null;
}

export interface ArchiveCursor {
  archivedAt: string;
  id: string;
}

export interface ArchiveListResult {
  items: ArchiveItem[];
  nextCursor: ArchiveCursor | null;
}

export interface ArchiveListOptions {
  limit?: number;
  cursor?: ArchiveCursor | null;
}

const DEFAULT_PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 50;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const TIMESTAMPTZ_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/;

function sanitizeSearchTerm(search: string): string {
  return search
    .trim()
    .replace(/[,()"'\\]/g, ' ')
    .replace(/[%_]/g, '\\$&')
    .replace(/\s+/g, ' ')
    .trim();
}

function mapArchiveItem(row: {
  item_type: string | null;
  source_id: string | null;
  title: string | null;
  preview_text: string | null;
  parent_title: string | null;
  archived_at: string | null;
  updated_at: string | null;
}): ArchiveItem | null {
  if (
    (row.item_type !== 'project' && row.item_type !== 'conversation') ||
    !row.source_id ||
    !row.title ||
    !row.archived_at
  ) {
    return null;
  }

  return {
    id: row.source_id,
    kind: row.item_type,
    title: row.title,
    preview: row.preview_text,
    parentTitle: row.parent_title,
    archivedAt: row.archived_at,
    updatedAt: row.updated_at,
  };
}

/**
 * Read-model boundary for Settings → Archive. Archive state stays on the
 * source tables; this service only reads the security-invoker union view so UI
 * code never needs to know how Projects and private conversations are stored.
 */
class ArchiveServiceClass extends EnhancedBaseService {
  async listItems(
    scope: ArchiveScope = 'all',
    search = '',
    options: ArchiveListOptions = {},
  ): Promise<ServiceResponse<ArchiveListResult>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const limit = Math.min(
        Math.max(Math.trunc(options.limit ?? DEFAULT_PAGE_SIZE), 1),
        MAX_PAGE_SIZE,
      );
      let query = this.client
        .from('archived_items_index')
        .select('*')
        .order('archived_at', { ascending: false })
        .order('source_id', { ascending: false })
        .limit(limit + 1);

      if (scope === 'projects') query = query.eq('item_type', 'project');
      if (scope === 'conversations') query = query.eq('item_type', 'conversation');

      const term = sanitizeSearchTerm(search);
      if (term) {
        query = query.or(
          `title.ilike.%${term}%,preview_text.ilike.%${term}%,parent_title.ilike.%${term}%`,
        );
      }

      if (options.cursor) {
        const parsedArchivedAt = Date.parse(options.cursor.archivedAt);
        if (
          !TIMESTAMPTZ_PATTERN.test(options.cursor.archivedAt) ||
          !Number.isFinite(parsedArchivedAt) ||
          !UUID_PATTERN.test(options.cursor.id)
        ) {
          throw new Error('Archive cursor is invalid');
        }
        // Keep the exact Postgres timestamp. Re-emitting it through JS Date
        // truncates microseconds and can skip tied rows at the page boundary.
        const archivedAt = options.cursor.archivedAt;
        const id = options.cursor.id;
        query = query.or(
          `archived_at.lt.${archivedAt},and(archived_at.eq.${archivedAt},source_id.lt.${id})`,
        );
      }

      const { data, error } = await query;
      if (error) throw new Error(`Archive list failed: ${error.message}`);

      const mappedItems = (data ?? [])
        .map(mapArchiveItem)
        .filter((item): item is ArchiveItem => item !== null);
      const hasMore = mappedItems.length > limit;
      const items = mappedItems.slice(0, limit);
      const lastItem = items.at(-1);

      return {
        items,
        nextCursor:
          hasMore && lastItem ? { archivedAt: lastItem.archivedAt, id: lastItem.id } : null,
      };
    });
  }
}

export const archiveService = new ArchiveServiceClass();
